import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Checkbox, FormControlLabel, CircularProgress } from '@mui/material';
import { Modal, Dropdown, Button, Input } from '../../components/Common';
import { PageHeader } from '../../components/Layout';
import { CitiesTable } from '../../components/Tables';
import { useCities } from '../../hooks/useCities';
import { City } from '../../types/city.types';
import { toast } from 'react-toastify';

interface CityFormState {
  name: string;
  isDefault: boolean;
}

const emptyForm: CityFormState = { name: '', isDefault: false };

const Cities: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      toast.error('Authentication required. Please log in.');
      window.location.href = '/auth';
    } else {
      setIsAuthenticated(true);
    }
  }, []);

  const {
    cities,
    loading,
    createCity,
    updateCity,
    deleteCity,
    refetch: refetchCities,
    reorderCities: apiReorderCities,
  } = useCities();

  const [localCities, setLocalCities] = useState<City[]>(cities);

  // Sync local list whenever the API data changes (create/update/delete/refetch)
  useEffect(() => {
    setLocalCities(cities);
  }, [cities]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [formState, setFormState] = useState<CityFormState>(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [cityToDelete, setCityToDelete] = useState<City | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const selectedCity = useMemo(
    () => cities.find((city) => city.cityId === selectedItem) || null,
    [cities, selectedItem]
  );

  const openCreateModal = () => {
    if (!isAuthenticated) {
      toast.error('Authentication required to add cities');
      return;
    }
    setIsEditMode(false);
    setEditingCity(null);
    setFormState(emptyForm);
    setIsModalOpen(true);
  };

  const openEditModal = (city: City) => {
    if (!isAuthenticated) {
      toast.error('Authentication required to edit cities');
      return;
    }
    setIsEditMode(true);
    setEditingCity(city);
    setFormState({ name: city.name, isDefault: city.isDefault });
    setIsModalOpen(true);
    handleMenuClose();
  };

  const handleMakeDefault = async (city: City) => {
    if (!isAuthenticated) {
      toast.error('Authentication required');
      return;
    }
    handleMenuClose();
    try {
      await updateCity(city.cityId, { isDefault: true });
    } catch (error) {
      // Error toast already shown by the hook
    }
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      toast.error('Authentication required');
      return;
    }
    if (!formState.name.trim()) {
      toast.error('City name is required');
      return;
    }
    setIsSubmitting(true);
    try {
      if (isEditMode && editingCity) {
        await updateCity(editingCity.cityId, {
          name: formState.name.trim(),
          isDefault: formState.isDefault,
        });
      } else {
        await createCity({
          name: formState.name.trim(),
          isDefault: formState.isDefault,
        });
      }
      setIsModalOpen(false);
      setEditingCity(null);
      setFormState(emptyForm);
    } catch (error) {
      // Error toast already shown by the hook
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (city: City) => {
    if (!isAuthenticated) {
      toast.error('Authentication required to delete cities');
      return;
    }
    setCityToDelete(city);
    setIsDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    if (!cityToDelete) return;
    setIsDeleting(true);
    try {
      await deleteCity(cityToDelete.cityId);
      setIsDeleteConfirmOpen(false);
      setCityToDelete(null);
      refetchCities();
    } catch (error) {
      // Error toast already shown by the hook
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteConfirmOpen(false);
    setCityToDelete(null);
  };

  const handleCitiesReorder = (reordered: City[]) => {
    setLocalCities(reordered);
    apiReorderCities(reordered);
  };

  return (
    <Box
      className="page-container"
      sx={{
        padding: {
          xs: '24px 16px',
          sm: '40px 40px',
          md: '40px 80px',
        },
        gap: '24px',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Page Header */}
      <PageHeader
        title="Cities"
        actionButtonText="New city"
        onActionClick={openCreateModal}
      />

      {/* Cities Table */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'stretch', gap: '32px', width: '100%', flex: 1, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress sx={{ color: 'var(--palette-light-brand-primary-value-hex)' }} />
          </Box>
        ) : (
          <CitiesTable
            cities={localCities}
            onReorder={handleCitiesReorder}
            onMenuOpen={handleMenuOpen}
          />
        )}
      </Box>

      {/* Actions Menu */}
      <Dropdown
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        minWidth={180}
        options={[
          {
            label: 'Set as default',
            value: 'make-default',
            disabled: selectedCity?.isDefault || false,
            onClick: () => {
              if (selectedCity) handleMakeDefault(selectedCity);
            },
          },
          {
            label: 'Edit',
            value: 'edit',
            onClick: () => {
              if (selectedCity) openEditModal(selectedCity);
            },
          },
          {
            label: 'Delete',
            value: 'delete',
            color: 'danger',
            onClick: () => {
              if (selectedCity) handleDeleteClick(selectedCity);
            },
          },
        ]}
      />

      {/* Add/Edit City Modal */}
      <Modal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingCity(null);
          setFormState(emptyForm);
        }}
        title={isEditMode ? 'Edit city' : 'New city'}
        width={508}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
              City name
            </Typography>
            <Input
              placeholder="e.g. Doha"
              value={formState.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFormState((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={formState.isDefault}
                onChange={(e) =>
                  setFormState((prev) => ({ ...prev, isDefault: e.target.checked }))
                }
                sx={{
                  color: 'var(--palette-light-brand-primary-value-hex)',
                  '&.Mui-checked': {
                    color: 'var(--palette-light-brand-primary-value-hex)',
                  },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                Set as default city (shown to users by default)
              </Typography>
            }
          />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
            <Button primary onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEditMode ? 'Save changes' : 'Create city'}
            </Button>
            <Button
              primary={false}
              bordered={false}
              onClick={() => {
                setIsModalOpen(false);
                setEditingCity(null);
                setFormState(emptyForm);
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteConfirmOpen}
        onClose={handleCancelDelete}
        title="Delete city"
        width={508}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-6-value)' }}>
          <Typography sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            lineHeight: '24px',
          }}>
            Are you sure you want to delete {cityToDelete?.name ? `"${cityToDelete.name}"` : 'this city'}?
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)', marginTop: 'var(--palette-light-4-value)' }}>
            <Button
              primary
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              sx={{
                width: '100%',
                backgroundColor: 'var(--palette-light-status-danger-value-hex)',
                '&:hover': {
                  backgroundColor: '#dc2626',
                },
              }}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
            <Button
              primary={false}
              bordered={false}
              onClick={handleCancelDelete}
              sx={{ width: '100%' }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default Cities;
